import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../seo/Seo';
import {
  HiOutlineShieldCheck,
  HiOutlineDocumentText,
  HiOutlineLockClosed,
  HiOutlineUserGroup,
  HiOutlineScale,
  HiOutlineSparkles,
  HiOutlineIdentification,
  HiOutlineAdjustments,
} from 'react-icons/hi';
import {
  LEGAL_ENTITY_NAME, REGISTERED_ADDRESS, GST_NUMBER,
  CONTACT_PHONE, CONTACT_PHONE_DISPLAY, CONTACT_EMAIL,
} from '../../constants/constants';
import { RATES, YEARLY_MONTHS_CHARGED } from '../pricing/pricingData';
import { SEAT_QUESTIONS, SEAT_ACTIONS } from '../ai/aiData';
import {
  NOTICE_VERSION, NOTICE_LAST_UPDATED, NOTICE_EFFECTIVE_FROM, DATA_FIDUCIARY,
  RETENTION, COOKIES, CONSENT_CATEGORIES,
} from '../../privacy/privacyConfig';
import { useConsent } from '../../privacy/ConsentContext';
import './PrivacyPolicy.css';

// The privacy notice comes first (it is what most visitors look for), then
// cookies, rights and grievances, then the Terms of Use and the Raya AI terms.
const SECTIONS = [
  { id: 'privacy', label: 'Privacy notice', Icon: HiOutlineShieldCheck },
  { id: 'rights', label: 'Your rights', Icon: HiOutlineIdentification },
  { id: 'cookies', label: 'Cookies & choices', Icon: HiOutlineAdjustments },
  { id: 'terms', label: 'Terms of Use', Icon: HiOutlineDocumentText },
  { id: 'ai', label: 'Raya AI Terms', Icon: HiOutlineSparkles },
  { id: 'contact', label: 'Contact', Icon: HiOutlineUserGroup },
];

// What we collect, why, on what basis and for how long — as Data Fiduciary.
const DATA_TABLE = [
  {
    what: 'Website enquiries',
    data: 'Name, company, email, phone and your message, from the contact form or an email to us.',
    why: 'To reply, arrange a demo or send a quote.',
    basis: 'Consent, given with the tick box on the form.',
    keep: RETENTION.enquiry,
  },
  {
    what: 'Sign-up and your account',
    data: 'Name, job title, work email, phone, company name, size, country and industry; sign-in details and two-factor set-up.',
    why: 'To create and secure your account, set it up for your industry and support your trial.',
    basis: 'Consent at sign-up; then the service you asked us to provide.',
    keep: RETENTION.signup,
  },
  {
    what: 'Billing',
    data: 'Billing contact, GSTIN, invoices, and payment references from our payment provider.',
    why: 'To bill you and meet GST and accounting law.',
    basis: 'Compliance with law, and the service you asked for.',
    keep: RETENTION.financial,
  },
  {
    what: 'Data requests',
    data: 'Name, email, mobile number and the details of your request.',
    why: 'To verify you, act on the request and show how we handled it.',
    basis: 'Consent, and compliance with the DPDP Act.',
    keep: RETENTION.privacyRequest,
  },
  {
    what: 'Privacy choices',
    data: 'Which cookie categories you allowed, when, and the notice version.',
    why: 'To respect your choice and prove we did.',
    basis: 'Strictly necessary; compliance with law.',
    keep: RETENTION.consentRecord,
  },
  {
    what: 'Security and technical logs',
    data: 'IP address, browser, device and timestamps when you use the site or the platform.',
    why: 'To keep the service running, secure and free of abuse.',
    basis: 'Needed to provide the service you use; consent for Google reCAPTCHA.',
    keep: RETENTION.technicalLogs,
  },
];

// Processors and other recipients. Named where the product documents name them.
const PROCESSORS = [
  { who: 'Microsoft Azure', what: 'Hosting, storage and backups for the website and the platform, in India.' },
  { who: 'Email and WhatsApp delivery providers', what: 'Sending account emails, one-time codes and messages you asked for. WhatsApp messages go through Meta’s WhatsApp Business Platform.' },
  { who: 'Razorpay', what: 'Taking payment for your Raya subscription. Card and bank details go to Razorpay, not to us.' },
  { who: 'Google reCAPTCHA', what: 'Bot protection on the sign-up and password-reset forms — only if you allow it.' },
  { who: 'AI model providers', what: 'Processing Raya AI requests your organisation makes, under contracts that forbid training on your data. The current list is available on request.' },
  { who: 'Professional advisers and authorities', what: 'Our auditors and lawyers under confidentiality, and Indian authorities or courts where the law requires it.' },
];

const Reveal = ({ children, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) { el.classList.add('revealed'); return undefined; }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('revealed'); observer.unobserve(el); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    const failsafe = setTimeout(() => el.classList.add('revealed'), 1200);
    return () => { observer.disconnect(); clearTimeout(failsafe); };
  }, []);
  return <div ref={ref} className={`reveal-on-scroll ${className}`}>{children}</div>;
};

const Callout = ({ children }) => <div className="privacy-callout">{children}</div>;

const SectionHead = ({ Icon, children }) => (
  <div className="privacy-section-header">
    <div className="section-icon-box" aria-hidden="true"><Icon size={24} /></div>
    <h2>{children}</h2>
  </div>
);

const LegalPage = () => {
  const [activeSection, setActiveSection] = useState('privacy');
  const { openPreferences, record } = useConsent();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY + 200;
      for (const { id } of SECTIONS) {
        const el = document.getElementById(id);
        if (el && scrollPos >= el.offsetTop && scrollPos < el.offsetTop + el.offsetHeight) {
          setActiveSection(id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="privacy-page">
      <Seo path="/privacy-policy" />

      <div className="privacy-layout">
        <aside className="privacy-sidebar">
          <div className="sidebar-title">On this page</div>
          <nav className="sidebar-nav" aria-label="Sections of this page">
            {SECTIONS.map(({ id, label, Icon }) => (
              <button
                type="button"
                key={id}
                className={`sidebar-link ${activeSection === id ? 'active' : ''}`}
                onClick={() => scrollTo(id)}
              >
                <Icon aria-hidden="true" /> {label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="privacy-content">
          <header>
            <Reveal>
              <h1>Privacy notice &amp; terms</h1>
              <p className="privacy-intro">
                What personal data we collect, why, who helps us process it, how long we keep it and how
                you can use your rights under India’s Digital Personal Data Protection Act, 2023 — followed
                by the Terms of Use and the Raya AI terms.
              </p>
              <p className="privacy-updated">
                This notice was last updated on <strong>{NOTICE_LAST_UPDATED}</strong> · Version {NOTICE_VERSION} · Effective from {NOTICE_EFFECTIVE_FROM}
              </p>
              <div className="privacy-meta">
                <span>{LEGAL_ENTITY_NAME}</span>
                <span aria-hidden="true">•</span>
                <span>Ahmedabad, India</span>
              </div>
            </Reveal>
          </header>

          {/* ─── PRIVACY NOTICE ───────────────────────────────────────────── */}
          <section className="privacy-section" id="privacy">
            <Reveal>
              <SectionHead Icon={HiOutlineLockClosed}>Privacy notice</SectionHead>

              <h3>1. Who we are</h3>
              <p>
                The Data Fiduciary for this website and for the accounts we hold with our customers is
                {' '}<strong>{DATA_FIDUCIARY.name}</strong>, {DATA_FIDUCIARY.address} (GSTIN {GST_NUMBER}).
                Write to us at <a href={`mailto:${DATA_FIDUCIARY.email}`}>{DATA_FIDUCIARY.email}</a>.
              </p>

              <h3>2. Our two roles</h3>
              <p>
                For the details you give us — on this website, at sign-up, and for billing — we decide why
                and how they are used, so we are the <strong>Data Fiduciary</strong>. For the business data
                your organisation puts into Raya ERP — its customers, suppliers, employees and transactions —
                your organisation is the Data Fiduciary and we are its <strong>Data Processor</strong>,
                acting only on its instructions. Requests about that data go to the organisation first.
              </p>

              <h3>3. What we collect, why, and for how long</h3>
              <div className="privacy-table-wrap">
                <table className="privacy-table">
                  <caption className="sr-only">Personal data we process as Data Fiduciary</caption>
                  <thead>
                    <tr><th scope="col">What</th><th scope="col">Data</th><th scope="col">Purpose</th><th scope="col">Basis</th><th scope="col">Kept</th></tr>
                  </thead>
                  <tbody>
                    {DATA_TABLE.map((r) => (
                      <tr key={r.what}><th scope="row">{r.what}</th><td>{r.data}</td><td>{r.why}</td><td>{r.basis}</td><td>{r.keep}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p>
                We collect only what each purpose needs, we do not sell personal data, and we do not use your
                business data to market to your customers.
              </p>

              <h3>4. The legal basis we rely on</h3>
              <ul>
                <li><strong>Consent</strong> (section 6): you give it with an unticked box on our forms or in Privacy choices. It is specific to the purpose shown, and you can withdraw it as easily as you gave it.</li>
                <li><strong>Legitimate uses</strong> (section 7): where you give us data voluntarily for a specific purpose — such as running the account you signed up for — and where the law requires us to keep or share data, for example GST records.</li>
              </ul>
              <p>Withdrawing consent stops future processing on that basis; it does not undo processing that already took place lawfully.</p>

              <h3>5. Who we share it with</h3>
              <p>We use these processors under contracts that limit them to our instructions and require them to keep the data secure:</p>
              <ul>
                {PROCESSORS.map((p) => <li key={p.who}><strong>{p.who}:</strong> {p.what}</li>)}
              </ul>
              <p>
                <strong>Outside India.</strong> We host in India. Some providers above — Google reCAPTCHA,
                Meta’s WhatsApp platform, email delivery and AI model providers — may process data outside
                India. The DPDP Act allows this except to countries the Central Government restricts, and we
                will not send personal data to any country so restricted.
              </p>

              <h3>6. Business data in the platform</h3>
              <p>
                Raya keeps each customer’s data logically separate, and every query — including one made by
                Raya AI — is scoped to that customer. AI prompt logs are off by default. When a user asks Raya
                AI to do something, the prompt and the records needed are sent to our AI provider and the
                result is returned; providers may not train on it and must delete it after serving the
                request, apart from any short-lived abuse-monitoring window they operate.
              </p>

              <h3>7. How long we keep data</h3>
              <ul>
                <li>Website enquiries: {RETENTION.enquiry}.</li>
                <li>Accounts: {RETENTION.signup}. After a subscription is cancelled, data is kept {RETENTION.afterCancellation}, then purged from live systems and rolled out of backups on the normal cycle.</li>
                <li>Financial records: {RETENTION.financial}.</li>
                <li>Data requests: {RETENTION.privacyRequest}. Privacy choices: {RETENTION.consentRecord}.</li>
              </ul>
              <p>When a purpose is over, or you withdraw consent, we erase the data unless the law requires us to keep it.</p>

              <h3>8. How we protect it</h3>
              <ul>
                <li>Data is encrypted in transit and at rest, and hosted in India.</li>
                <li>Access is role-based, least-privilege and logged; Multi-Factor Authentication is available to every user.</li>
                <li>Our staff access customer data only to give support or resolve an incident, and that access is recorded.</li>
                <li>Backups are taken regularly and restoration is tested.</li>
              </ul>

              <h3>9. If there is a breach</h3>
              <p>
                If a personal data breach happens, we will inform the Data Protection Board of India and each
                affected person, in the form and within the time the DPDP Rules set: what happened, the likely
                consequences, what we are doing about it and what you can do to protect yourself. Where we are
                a processor, we tell the customer organisation without delay so it can do the same.
              </p>

              <h3>10. Children</h3>
              <p>
                Raya is a business product. You must be 18 or over to sign up, and we do not knowingly collect
                personal data from children or track or target them. If we learn that we have collected a
                child’s data without verifiable consent from a parent or guardian, we delete it.
              </p>

              <h3>11. Changes to this notice</h3>
              <p>
                We will post changes here with a new version number and date. If a change affects how we use
                data you gave us with consent, we will ask again before it applies.
              </p>
            </Reveal>
          </section>

          {/* ─── YOUR RIGHTS ─────────────────────────────────────────────── */}
          <section className="privacy-section" id="rights">
            <Reveal>
              <SectionHead Icon={HiOutlineIdentification}>Your rights</SectionHead>
              <p>As a Data Principal under the DPDP Act, 2023, you can ask us to:</p>
              <ul>
                <li><strong>Access</strong> a summary of the personal data we process about you, what we do with it, and who we shared it with.</li>
                <li><strong>Correct, complete or update</strong> data that is inaccurate or out of date.</li>
                <li><strong>Erase</strong> data we no longer need, unless the law requires us to keep it.</li>
                <li><strong>Withdraw consent</strong> at any time — for cookies, from Privacy choices; for anything else, with a request.</li>
                <li><strong>Nominate</strong> someone to exercise these rights if you die or become unable to.</li>
                <li><strong>Raise a grievance</strong> with us, and then with the Data Protection Board of India.</li>
              </ul>
              <h3>How to use them</h3>
              <p>
                Use the <Link to="/privacy-request">data request form</Link>, or email{' '}
                <a href={`mailto:${DATA_FIDUCIARY.email}?subject=DPDP%20request`}>{DATA_FIDUCIARY.email}</a> with
                “DPDP request” in the subject. We acknowledge within 7 working days and aim to resolve within 30
                days. We may ask you to confirm who you are first. There is no charge.
              </p>
              <p>
                If your data sits in a customer organisation’s Raya account, please ask that organisation; we
                will help it respond but cannot act on its data without its instruction.
              </p>
              <h3>Grievances</h3>
              <p>
                If you are unhappy with how we handled your data or a request, email{' '}
                <a href={`mailto:${DATA_FIDUCIARY.email}?subject=Privacy%20grievance`}>{DATA_FIDUCIARY.email}</a> with
                “Privacy grievance” in the subject, or write to {DATA_FIDUCIARY.name}, {DATA_FIDUCIARY.address}. If
                you are not satisfied with our answer, you may complain to the Data Protection Board of India.
              </p>
              <p>
                You also have duties under the Act: give accurate information, do not impersonate anyone, and
                do not file false or frivolous complaints.
              </p>
            </Reveal>
          </section>

          {/* ─── COOKIES ─────────────────────────────────────────────────── */}
          <section className="privacy-section" id="cookies">
            <Reveal>
              <SectionHead Icon={HiOutlineAdjustments}>Cookies and your choices</SectionHead>
              <p>
                This website uses a short list of cookies and browser storage. Strictly necessary items are
                always on. Anything else — today only Google reCAPTCHA — loads only after you allow it, and
                no category is switched on for you in advance.
              </p>
              <div className="privacy-table-wrap">
                <table className="privacy-table">
                  <caption className="sr-only">Cookies and storage used by this website</caption>
                  <thead>
                    <tr><th scope="col">Name</th><th scope="col">Type and set by</th><th scope="col">Category</th><th scope="col">Purpose</th><th scope="col">How long</th></tr>
                  </thead>
                  <tbody>
                    {COOKIES.map((c) => (
                      <tr key={c.name}><th scope="row"><code>{c.name}</code></th><td>{c.kind} · {c.by}</td><td>{c.category}</td><td>{c.purpose}</td><td>{c.duration}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <h3>Categories</h3>
              <ul>
                {CONSENT_CATEGORIES.map((c) => <li key={c.key}><strong>{c.label}:</strong> {c.desc}</li>)}
              </ul>
              <p>
                Fonts are served from our own servers, so reading this site does not send your address to a
                font provider. The documentation at /docs and the interactive demo at /demo are separate
                pages and are not covered by this list.
              </p>
              <div className="privacy-choices">
                <button type="button" className="privacy-choices-btn" onClick={openPreferences}>Open privacy choices</button>
                <span>
                  {record
                    ? `Your choice was saved on ${new Date(record.timestamp).toLocaleDateString('en-IN', { dateStyle: 'medium' })}.`
                    : 'You have not made a choice on this device yet.'}
                </span>
              </div>
            </Reveal>
          </section>

          {/* ─── TERMS OF USE ────────────────────────────────────────────── */}
          <section className="privacy-section" id="terms">
            <Reveal>
              <div className="privacy-section-header">
                <div className="section-icon-box"><HiOutlineScale size={24} /></div>
                <h2>Terms of Use</h2>
              </div>
              <p>
                These terms govern your access to and use of the Raya ERP &amp; CRM platform, operated by
                {' '}{LEGAL_ENTITY_NAME}, {REGISTERED_ADDRESS} (GSTIN {GST_NUMBER}). By creating an
                account or using the service, you agree to them on behalf of your organisation.
              </p>

              <h3>1. The Service</h3>
              <p>
                Raya provides cloud software for inventory, sales and invoicing, purchase, finance,
                production, projects, HR and CRM, together with the optional Raya AI assistant described
                below. We grant you a non-exclusive, non-transferable, revocable licence to use the
                platform for your organisation&rsquo;s own internal business operations for as long as
                your subscription is active.
              </p>
              <p>
                A subscription covers one company — that is, one PAN. Every GSTIN registered under that
                PAN is included. A separate legal entity on its own PAN requires its own subscription.
              </p>

              <h3>2. Your account and security</h3>
              <ul>
                <li>You must be at least 18 years old to create an account.</li>
                <li>You are responsible for all activity under your account and for the accuracy of the users, roles and permissions you configure.</li>
                <li>We strongly recommend enabling our built-in Multi-Factor Authentication (MFA) for every user.</li>
                <li>Tell us promptly if you believe an account has been compromised.</li>
                <li>You agree not to reverse-engineer, scrape, resell, or attempt to compromise the security of our systems, or to use the platform to break the law.</li>
              </ul>

              <h3>3. Subscriptions, billing and taxes</h3>
              <ul>
                <li><strong>Per user, per month.</strong> Charges are based on the number of enabled users and the modules you have switched on. Rates are published on our pricing page.</li>
                <li><strong>Free trial.</strong> We offer a 14-day free trial. After it ends, a paid plan is required to keep your data active.</li>
                <li><strong>Add-ons.</strong> Optional modules can be switched on or off with effect from the next billing month. Raya ERP Standard is licensed whole, not in parts.</li>
                <li><strong>Yearly billing.</strong> If you pay annually in advance, you are billed for {YEARLY_MONTHS_CHARGED} months instead of 12. Annual fees paid in advance are not refundable on a pro-rata basis if you cancel mid-term, but you keep access for the rest of the term.</li>
                <li><strong>Raya AI.</strong> AI seats are charged separately at &#8377;{RATES.ai} per enabled seat, per month. Each seat adds {SEAT_QUESTIONS} questions and {SEAT_ACTIONS} actions to a pool shared across your company, which resets on the 1st of each month and does not carry over. Optional top-up packs may be purchased within the application.</li>
                <li><strong>Taxes.</strong> All published prices are exclusive of GST, which is charged additionally at the applicable rate against your GSTIN.</li>
                <li><strong>Price changes.</strong> We will give you at least 30 days&rsquo; written notice before a rate change takes effect, and it will never apply to a term you have already paid for.</li>
                <li><strong>Cancellation.</strong> You can cancel at any time from your account settings. Cancellation takes effect at the end of the current billing period.</li>
              </ul>

              <h3>4. Acceptable use</h3>
              <p>You agree not to use the platform to:</p>
              <ul>
                <li>Store or transmit malicious code, or interfere with anyone else&rsquo;s use of the service.</li>
                <li>Upload personal data you have no lawful basis to process.</li>
                <li>Create records that you know to be false, or to misrepresent transactions to a tax or regulatory authority.</li>
                <li>Exceed documented rate limits or run automated load that degrades the service for others.</li>
              </ul>

              <h3>5. Ownership</h3>
              <p>
                We own the platform software, its design and everything we develop. You own the data you
                put into it and the records generated from it, including records created for you by Raya
                AI. We claim no licence over your business data other than what is strictly needed to run
                the service for you, keep it secure, and meet a legal obligation.
              </p>

              <h3>6. Availability and support</h3>
              <p>
                We aim to keep the platform available at all times and will give advance notice of planned
                maintenance wherever practical. Support is provided by email and phone during Indian
                business hours. Where we have agreed a specific service level with you in writing, that
                agreement takes precedence over this paragraph.
              </p>

              <h3>7. Suspension, termination and getting your data out</h3>
              <p>
                We may suspend an account for non-payment or for a serious breach of these terms, and will
                tell you why. On termination for any reason, you have 30 days to export your data, after
                which it is deleted in line with the retention rules in the privacy notice above. Export is
                available to you at any time while the subscription is active.
              </p>

              <h3>8. Warranties and liability</h3>
              <p>
                The platform is provided on an &ldquo;as is&rdquo; basis. We do not warrant that it will be
                uninterrupted or error-free, and nothing in the service is accounting, tax or legal advice.
                To the extent permitted by law, our aggregate liability arising out of or relating to the
                service is limited to the fees you paid us in the twelve months before the event giving
                rise to the claim, and neither party is liable for indirect or consequential loss. Nothing
                here limits liability that cannot be limited under Indian law.
              </p>

              <h3>9. Changes to these terms</h3>
              <p>
                We may update these terms as the product changes. If a change materially affects your
                rights, we will give notice in the application or by email before it takes effect.
                Continuing to use the platform after that means you accept the updated terms.
              </p>

              <h3>10. Governing law</h3>
              <p>
                These terms are governed by the laws of India. The courts at Ahmedabad, Gujarat have
                exclusive jurisdiction over any dispute arising from them.
              </p>
            </Reveal>
          </section>

          {/* ─── RAYA AI TERMS ────────────────────────────────────────────── */}
          <section className="privacy-section" id="ai">
            <Reveal>
              <div className="privacy-section-header">
                <div className="section-icon-box"><HiOutlineSparkles size={24} /></div>
                <h2>Raya AI Terms</h2>
              </div>
              <p>
                Raya AI is an optional assistant that answers questions about your data and prepares work
                in it — filling in bills, expense vouchers, invoices, quotations and orders, finding stock,
                and producing forecasts. It prepares; a person approves. These additional terms apply
                whenever it is used.
              </p>

              <h3>1. It acts as your user, never above them</h3>
              <ul>
                <li>Raya AI can only read and write what the user who asked is already permitted to read and write. It holds no separate privileges of its own.</li>
                <li><strong>Nothing is saved to your books without a person.</strong> The assistant prepares a document and stops; a user with the right permission must approve it before it is posted. You can disable drafting entirely, or set a value above which it will refuse to draft at all.</li>
                <li>Every action is written to the audit trail alongside the prompt that produced it, the steps taken, and the user on whose behalf it acted.</li>
                <li>Anything approved and posted can be reversed through the same process as any other entry.</li>
                <li>An administrator can switch Raya AI off for the whole company at any time, and can assign or remove an individual user’s AI seat from one month to the next.</li>
              </ul>

              <h3>2. You remain responsible for your records</h3>
              <Callout>
                <strong>Review before you rely on it.</strong> Raya AI produces drafts and entries from
                the information available to it. You remain responsible for the accuracy of your books
                and for anything filed with a tax or regulatory authority. Documents and figures produced
                with AI assistance should be reviewed by a competent person before they are submitted,
                sent to a customer, or relied on for a business decision.
              </Callout>
              <p>
                Raya AI is not a substitute for professional judgement, and its output is not accounting,
                tax, legal or financial advice. Statutory filings remain yours to verify and sign.
              </p>

              <h3>3. Accuracy and limitations</h3>
              <ul>
                <li>AI output can be incomplete or wrong, particularly where your masters, rates or history are incomplete.</li>
                <li>Where a vendor, item, rate or ledger is ambiguous, Raya AI is designed to ask rather than guess — but this is a safeguard, not a guarantee.</li>
                <li>Forecasts are estimates derived from your own history. They describe a likely outcome, not a promised one, and should not be the sole basis of a purchasing or credit decision.</li>
                <li>We may change, improve or withdraw individual AI capabilities as the underlying technology changes.</li>
              </ul>

              <h3>4. How your data is used to answer a request</h3>
              <p>
                To carry out a request, Raya AI sends the prompt and the specific records needed to answer
                it to our AI provider for processing, served from infrastructure located in India. That
                processing is governed by a contract that prohibits using your content to train any model
                and requires deletion after the request is served. Your books are never training data — for
                us or for anyone else. Administrators can choose which model provider serves their
                requests, and the current list of AI subprocessors is set out in the privacy notice above
                and is available on request.
              </p>
              <p>
                If you would prefer that no data leave the platform for AI processing at all, an
                administrator can switch Raya AI off for the whole company. The rest of the platform works
                exactly the same way without it.
              </p>

              <h3>5. Acceptable use of AI</h3>
              <ul>
                <li>Do not use Raya AI to generate records you know to be false, or to disguise the nature of a transaction.</li>
                <li>Do not use it to produce content that is unlawful, or to make decisions about individuals that require human review under applicable law.</li>
                <li>Do not attempt to extract the underlying model, its instructions, or data belonging to another customer.</li>
              </ul>

              <h3>6. What it costs</h3>
              <p>
                Raya AI is &#8377;{RATES.ai} per enabled seat, per month. Seats can be added or dropped from
                one month to the next, and each one adds {SEAT_QUESTIONS} questions and {SEAT_ACTIONS} actions
                to the pool your company shares.
              </p>
            </Reveal>
          </section>


          {/* ─── CONTACT ─────────────────────────────────────────────────── */}
          <section className="privacy-section" id="contact">
            <Reveal>
              <div className="privacy-contact-card">
                <SectionHead Icon={HiOutlineUserGroup}>Questions about this page</SectionHead>
                <p>About our terms, Raya AI or your data — write or call us.</p>
                <div className="privacy-contact-grid">
                  <div className="contact-item">
                    <span className="contact-label">Email</span>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="contact-value">{CONTACT_EMAIL}</a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-label">Call us</span>
                    <a href={`tel:${CONTACT_PHONE}`} className="contact-value">{CONTACT_PHONE_DISPLAY}</a>
                  </div>
                  <div className="contact-item">
                    <span className="contact-label">Registered office</span>
                    <span className="contact-value" style={{ color: '#1d1d1f' }}>{REGISTERED_ADDRESS}</span>
                  </div>
                </div>
              </div>
            </Reveal>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
